import markerSDK from '@marker.io/browser'

type Widget = Awaited<ReturnType<typeof markerSDK.loadWidget>>

export interface LoadMarkerOptions {
  /** Marker.io project/destination ID. Empty string skips loading. */
  projectId: string | undefined
  /**
   * Optional user info to auto-fill the bug report form. Call after Clerk
   * sign-in with the current user's email + id.
   */
  reporter?: { email: string; fullName: string }
  /**
   * Custom data attached to every submitted bug report. Good place to tag
   * the app name so the inbound webhook can route tickets correctly.
   */
  customData?: Record<string, string>
}

let widgetPromise: Promise<Widget> | null = null

/**
 * Load the Marker.io bug-report widget. Safe to call with an empty project
 * ID (no-op). Idempotent per page load — subsequent calls return the same
 * widget instance.
 */
export function loadMarkerWidget(opts: LoadMarkerOptions): Promise<Widget | null> {
  if (!opts.projectId) {
    console.info('[observability] Marker project ID not provided — skipping widget load')
    return Promise.resolve(null)
  }

  if (!widgetPromise) {
    widgetPromise = markerSDK.loadWidget({
      project: opts.projectId,
      ...(opts.reporter ? { reporter: opts.reporter } : {}),
      ...(opts.customData ? { customData: opts.customData } : {}),
    })
  }
  return widgetPromise
}

/**
 * Update reporter info on an already-loaded widget. Call after a user signs
 * in to auto-fill the form. No-op if the widget isn't loaded or reporter
 * info is incomplete (Marker requires both email + fullName).
 */
export async function setMarkerReporter(reporter: {
  email?: string
  fullName?: string | null
}): Promise<void> {
  if (!widgetPromise) return
  if (!reporter.email || !reporter.fullName) return
  const widget = await widgetPromise
  widget?.setReporter({ email: reporter.email, fullName: reporter.fullName })
}
